<template>
  <b-card class="rank-panel">
    <h1 class="panel__title">mDory Score</h1>

    <div class="legend">
      <ol class="legend__items">
        <li class="legend__item" v-for="n in 9" :key="n">
          <div class="color-swatch" :class="'swatch-' + n"></div>
        </li>
      </ol>

      <div class="legend__labels">
        <div class="legend__label -start">0</div>
        <div class="flex-rule"></div>
        <div class="legend__label -end">100</div>
      </div>
    </div>

    <b-form-group>
      <template v-slot:label>
        <div class="d-flex justify-content-between">
          <div class="field-label__name">Property Kind</div>
          <div class="field-label__value">{{ propertyKindDisplayNames[propertykind] }}</div>
        </div>
      </template>
      <b-button-group size="sm" class="w-100 mt-1 solid">
        <b-button v-for="(name, id) in propertyKindDisplayNames"
          variant="outline-primary"
          :key="id"
          :pressed="getPropKindActive(id)"
          @click="setPropKind(id)"
          v-b-tooltip.hover
          :title="name">
          <i class="fa" :class="getPropKindIcon(id)"></i>
          <span class="sr-only">{{ name }}</span>
        </b-button>
      </b-button-group>
    </b-form-group>

    <b-form-group>
      <template v-slot:label>
        <div class="d-flex mt-2 mb-3">
          <div><i class="fa fa-lg fa-minus text-danger"></i></div>
          <div class="flex-rule"></div>
          <div>
            <span class="text-muted text-uppercase font-weight-bold">Suitability</span>
            <i class="far fa-question-circle text-info ml-1" v-b-tooltip.hover title="Tooltip info explaining suitability"></i>
          </div>
          <div class="flex-rule"></div>
          <div><i class="fa fa-lg fa-plus text-primary"></i></div>
        </div>
      </template>

      <b-form-group v-for="slide in sliders[propertykind]" :key="slide.attr">
        <template v-slot:label>
          <div class="d-flex justify-content-between">
            <div class="field-label__name">
              {{ slide.title }}
              <i class="far fa-question-circle text-info" v-b-tooltip.hover title="Explanation of this demographic category goes here"></i>
            </div>
            <div class="field-label__value" :class="getValueClass(slide.model)">{{ valueDisplayFormatter(slide.model) }}</div>
          </div>
        </template>
        <vue-slider ref="slide.ref"
          v-model="slide.model"
          tooltip="none"
          :tooltip-formatter="valueDisplayFormatter"
          :process="process"
          :max="200"
          :min="0"
          :interval="10"
          @change="$emit('weightChange', slide.attr, $event)"></vue-slider>
      </b-form-group>

      <b-button type="reset" variant="outline-danger" block size="sm" @click="resetAll">Reset All</b-button>
    </b-form-group>

  </b-card>
</template>

<script>
import vueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
export default {
    name: 'app',
    components: {
        vueSlider
    },
    mounted: function() {
        this.propertykind = 'industrial';
    },
    data() {
        return {
            propertykind: '',
            propertyKindDisplayNames: {
              industrial: 'Industrial',
              studenthousing: 'Student Housing',
              retail: 'Commercial'
            },
            sliders: this.getInitialSliders()
        }
    },
    watch: {
        propertykind: function(val) {
            let self = this;
            //emit the only demographics that we care about
            self.$emit('propSelection', self.sliders[val]);
            self.sliders[val].forEach( (slide) => {
                self.$emit('weightChange', slide.attr, slide.model);
            });
        }
    },
    methods: {
        setupNewAttribs(event) {
        },
        sliderCallback(sliderRef, attrName) {
        let value = this.$refs[sliderRef].getValue();
        },

        getPropKindActive(id) {
          return this.propertykind == id;
        },

        getPropKindIcon(id) {
          switch (id) {
            case 'industrial':
              return 'fa-industry'
            case 'studenthousing':
              return 'fa-building';
            case 'retail':
              return 'fa-store'
          }
        },

        setPropKind(id) {
          this.propertykind = id;
        },

        valueDisplayFormatter: val => {
          val = (val / 100 - 1).toFixed(1);
          return (val > 0) ? ('+' + val) : val;
        },

        getValueClass(val) {
          if (val < 100) return 'text-danger';
          if (val == 100) return null;
          if (val > 100) return 'text-primary';
        },

        getProcessColor(dotsPos) {
          return (dotsPos[0] >= 50) ? '' : '#d23129';
        },

        process(dotsPos) {
          return [
            [50, dotsPos[0], { backgroundColor: this.getProcessColor(dotsPos) }],
          ];
        },

        getInitialSliders() {
          return {
            'industrial': [
              {ref: 'slider1', model: 100, attr: 'pop', title:'Population'},
              {ref: 'slider3', model: 100, attr: 'pct_dev', title:'Percent Developed'},
              {ref: 'slider4', model: 100, attr: 'ap_dist', title:'Close to Airports', 'inverse': true},
              {ref: 'slider15', model: 100, attr: 'rr_dist', title:'Close to Railroads', 'inverse': true},
              {ref: 'slider16', model: 100, attr: 'ur_dist', title:'Close to Urban Centers'},
              {ref: 'slider17', model: 100, attr: 'unempl', title:'Unemployment'},
              {ref: 'slider18', model: 100, attr: 'hs1844', title:'18-44 yo H.S.'},
            ],'studenthousing': [
              {ref: 'slider5', model: 100, attr: 'ur_dist', title:'Close to Urban Centers'},
              {ref: 'slider6', model: 100, attr: 'pct_dev', title:'Percent Developed'},
              {ref: 'slider7', model: 100, attr: 'walk', title:'Walkability'},
              {ref: 'slider8', model: 100, attr: 'pop_sq', title:'Population Density'},
              {ref: 'slider9', model: 100, attr: 'home_v', title:'Median Home Value'},
            ],'retail': [
              {ref: 'slider10', model: 100, attr: 'pct_dev', title:'Percent Developed'},
              {ref: 'slider11', model: 100, attr: 'walk', title:'Walkability'},
              {ref: 'slider12', model: 100, attr: 'pop_sq', title:'Population Density'},
              {ref: 'slider13', model: 100, attr: 'wk_fin', title:'Workforce: Finance'},
              {ref: 'slider14', model: 100, attr: 'wk_fod', title:'Workforce: Food Services'}
            ]}
        },

        resetAll() {
          this.sliders = this.getInitialSliders();
        }
      }
    }
</script>

<style lang="scss">
  .rank-panel {
    font-size: .75rem;
    left: 1em;
    max-height: calc(100vh - 2em);
    overflow-y: auto;
    top: 1rem;
    width: 240px;

    .form-group:last-child {
      margin-bottom: 0;
    }

    legend, label {
      padding-bottom: 0;
    }

    .vue-slider-component {
      margin: 0 -8px;
    }

    .vue-slider-rail {
      background-color: rgba(black, (1/8));
    }
  }

  .color-swatch {
    display: block;
    height: .75rem;
    width: 100%;
  }

  .swatch-1 { background-color: #FFFFD9; }
  .swatch-2 { background-color: #EDF8B1; }
  .swatch-3 { background-color: #C7E9B4; }
  .swatch-4 { background-color: #7FCDBB; }
  .swatch-5 { background-color: #41B6C4; }
  .swatch-6 { background-color: #1D91C0; }
  .swatch-7 { background-color: #225EA8; }
  .swatch-8 { background-color: #253494; }
  .swatch-9 { background-color: #081d58; }

  .swatch-label {
    font-size: .5rem;
    margin-left: 1em;
  }

  .flex-rule {
    align-self: center;
    background-color: rgba(black, .1);
    flex: 1 1 auto;
    height: 1px;
    margin: 0 .5rem;
  }

  .legend {
    margin: -.5rem 0 1rem;
  }

  .legend__items {
    align-items: center;
    border-radius: .25rem;
    box-shadow: 0 1px 1px rgba(black, .1);
    display: flex;
    list-style: none;
    margin: 0;
    overflow: hidden;
    padding: 0;
  }

  .legend__labels {
    display: flex;
    justify-content: space-between;
    margin: .5rem 0 0;
  }

  .legend__label {
    font-weight: bold;
    line-height: 1;
  }

  .legend__item {
    align-items: center;
    display: flex;
    flex: 1 0 0;
    white-space: nowrap;
  }

  .field-label__name {
    flex-shrink: 0;
  }

  .field-label__value {
    font-weight: bold;
    text-align: right;
  }
</style>
